import React, { useState, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { Modal } from '@components/molecules/Modal';
import ErrorMessage from '@components/molecules/ErrorMessage';
import PaymentMethodSelector from '@pages/register/components/PaymentMethodSelector';
import {
  useReactivateSubscription,
  ReactivateSubscriptionInput,
} from '@hooks/api/subscription/useReactivateSubscription';
import { usePaymentMethods } from '@hooks/api/subscription/usePaymentMethods';
import { useSubscriptionPlans } from '@hooks/api/subscription/useSubscriptionPlans';
import { useAppDispatch } from '@redux/hooks';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { OrganizationStatus } from '@models/organization.model';
import {
  PaymentMethodList,
  BillingPeriodSelector,
  PlanSelectionCards,
  PriceSummary,
  reactivateNewCardSchema,
  reactivateExistingCardSchema,
} from '@components/organisms/subscription';
import { useTranslation } from '@hooks/useTranslation';

interface ReactivateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  organizationId: string;
  organizationName: string;
  userData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  /**
   * If true, skip the automatic redirect after success.
   * Just refresh the data and close the modal.
   */
  skipRedirect?: boolean;
}

const ReactivateSubscriptionModal: React.FC<ReactivateSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  organizationName,
  userData,
  skipRedirect = false,
}) => {
  // Sprint 28, story-143. Bucket 2 — docs/architecture.md "## Sprint 28" Decision 4.
  const tOrgLifecycle = useTranslation('orgLifecycle');
  const tCommon = useTranslation('common');
  const [error, setError] = useState<string | null>(null);
  const [useExistingCard, setUseExistingCard] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const { mutateAsync, isPending } = useReactivateSubscription({ skipRedirect });
  // Pass organizationId to fetch payment methods for the specific organization being reactivated
  // Only fetch when modal is open to avoid FORBIDDEN errors for SUPER_ADMIN users
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = usePaymentMethods({
    organizationId,
    enabled: isOpen,
  });
  // Fetch subscription plans - only when modal is open
  const { data: subscriptionPlans, isLoading: isLoadingPlans } = useSubscriptionPlans({ enabled: isOpen });
  const dispatch = useAppDispatch();

  // Filter public plans (not addons)
  const publicPlans = useMemo(() => {
    if (!subscriptionPlans) return [];
    return subscriptionPlans.filter((plan) => plan.isPublic && !plan.isAddOn);
  }, [subscriptionPlans]);

  // Get all existing payment methods (multiple allowed per organization)
  const existingPaymentMethods = useMemo(() => {
    return paymentMethods || [];
  }, [paymentMethods]);

  // Check if there are any existing payment methods
  const hasExistingPaymentMethods = existingPaymentMethods.length > 0;

  // Set useExistingCard to true by default if there are existing payment methods
  // Also auto-select the default payment method
  useEffect(() => {
    if (hasExistingPaymentMethods && !isLoadingPaymentMethods) {
      setUseExistingCard(true);
      // Select the default payment method, or first one if no default
      const defaultMethod = existingPaymentMethods.find(pm => pm.isDefault) || existingPaymentMethods[0];
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.paymentMethodId);
      }
    }
  }, [hasExistingPaymentMethods, existingPaymentMethods, isLoadingPaymentMethods]);

  // Use the appropriate validation schema based on selection
  const validationSchema = useMemo(() => {
    return useExistingCard ? reactivateExistingCardSchema : reactivateNewCardSchema;
  }, [useExistingCard]);

  const formik = useFormik({
    initialValues: {
      paymentMethodType: 'credit_card',
      billingPeriod: 'MONTHLY',
      selectedPlanId: '',
      billingFirstName: userData?.firstName || '',
      billingLastName: userData?.lastName || '',
      billingEmail: userData?.email || '',
      billingAddress1: '',
      billingAddress2: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      billingCountry: 'US',
      billingPhone: '',
      ccNumber: '',
      ccExp: '',
      cvv: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError(null);

      try {
        let input: ReactivateSubscriptionInput;

        if (useExistingCard && selectedPaymentMethodId) {
          // Use selected existing payment method
          input = {
            organizationId,
            billingCycle: values.billingPeriod.toUpperCase(),
            planId: values.selectedPlanId || undefined,
            useExistingPaymentMethod: true,
            existingPaymentMethodId: selectedPaymentMethodId,
          };
        } else {
          // Create new payment method
          input = {
            organizationId,
            paymentMethodType: values.paymentMethodType.toUpperCase(),
            billingCycle: values.billingPeriod.toUpperCase(),
            planId: values.selectedPlanId || undefined,
            firstName: values.billingFirstName,
            lastName: values.billingLastName,
            email: values.billingEmail,
            address1: values.billingAddress1,
            address2: values.billingAddress2 || undefined,
            city: values.billingCity,
            state: values.billingState,
            zip: values.billingZip,
            country: values.billingCountry || 'US',
            phone: values.billingPhone || undefined,
            ccNumber: values.ccNumber?.replace(/\s/g, ''),
            ccExp: values.ccExp?.replace(/\//g, ''),
            cvv: values.cvv,
          };
        }

        await mutateAsync(input);

        // Update Redux state with ENABLED status
        dispatch(setOrganizationData({
          organizationId,
          name: organizationName,
          status: OrganizationStatus.ENABLED,
        }));

        formik.resetForm();
        onSuccess?.();

        // If skipRedirect is true, close the modal (the hook won't redirect)
        if (skipRedirect) {
          onClose();
        }
        // Note: If not skipping redirect, the hook handles the redirect with window.location.href
      } catch (err: any) {
        setError(
          err?.response?.errors?.[0]?.message ||
            err?.message ||
            tOrgLifecycle.reactivateFailedError,
        );
      }
    },
  });
  // `formik` is rebuilt every render, but setFieldValue is not: Formik wraps it in
  // its own useEventCallback, so it is stable and safe to name as a dependency.
  const { setFieldValue } = formik;

  // Auto-select first public plan if none selected
  useEffect(() => {
    if (publicPlans.length > 0 && !formik.values.selectedPlanId) {
      setFieldValue('selectedPlanId', publicPlans[0].id, false);
    }
  }, [publicPlans, setFieldValue, formik.values.selectedPlanId]);

  // Get selected plan
  const selectedPlan = useMemo(() => {
    if (!publicPlans.length) return null;
    if (formik.values.selectedPlanId) {
      return publicPlans.find((p) => p.id === formik.values.selectedPlanId) || publicPlans[0];
    }
    return publicPlans[0];
  }, [publicPlans, formik.values.selectedPlanId]);

  // Calculate pricing based on billing period and selected plan
  const isAnnual = formik.values.billingPeriod === 'ANNUAL';
  const monthlyCost = selectedPlan ? selectedPlan.basePriceCents / 100 : 0;
  const displayPrice = isAnnual ? monthlyCost * 12 : monthlyCost;

  const handleClose = () => {
    if (!isPending) {
      setError(null);
      formik.resetForm();
      setUseExistingCard(false);
      setSelectedPaymentMethodId(null);
      onClose();
    }
  };

  const handleSelectPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
    setUseExistingCard(true);
  };

  const handleAddNewCard = () => {
    setUseExistingCard(false);
    setSelectedPaymentMethodId(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={tOrgLifecycle.reactivateSubscription}
      description={tOrgLifecycle.reactivateSubscriptionFor.replace('{org}', organizationName)}
      size="large"
      cancelText={tCommon.cancel}
      confirmText={tOrgLifecycle.reactivateSubscription}
      handleConfirm={formik.submitForm}
      isLoading={isPending || isLoadingPaymentMethods || isLoadingPlans}
      disableConfirm={!formik.isValid || isPending || isLoadingPaymentMethods || isLoadingPlans}
      closeOnOverlayClick={false}
      closeOnEscape={!isPending}>
      <div className="space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {tOrgLifecycle.reactivateInfoBanner.replace('{org}', organizationName)}
          </p>
        </div>

        {/* Error Message */}
        {error && <ErrorMessage message={error} />}

        {/* Payment Method Selection */}
        {hasExistingPaymentMethods && (
          <PaymentMethodList
            paymentMethods={existingPaymentMethods}
            selectedPaymentMethodId={selectedPaymentMethodId}
            onSelectPaymentMethod={handleSelectPaymentMethod}
            showAddNewOption={true}
            isAddingNew={!useExistingCard}
            onAddNew={handleAddNewCard}
            isLoading={isLoadingPaymentMethods}
            disabled={isPending}
          />
        )}

        {/* Show PaymentMethodSelector when adding new card or no existing cards */}
        {(!hasExistingPaymentMethods || !useExistingCard) && (
          <PaymentMethodSelector
            formik={formik}
            userData={userData}
            defaultBillingCycle="monthly"
          />
        )}

        {/* Show Billing Period, Plan Selection, and Price Summary when using existing card */}
        {hasExistingPaymentMethods && useExistingCard && (
          <>
            <BillingPeriodSelector
              selectedPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
              onPeriodChange={(period) => formik.setFieldValue('billingPeriod', period)}
              monthlyCost={monthlyCost}
              showPricing={true}
              disabled={isPending}
            />

            <PlanSelectionCards
              plans={publicPlans}
              selectedPlanId={formik.values.selectedPlanId || publicPlans[0]?.id || null}
              onSelectPlan={(planId) => formik.setFieldValue('selectedPlanId', planId)}
              billingPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
              isLoading={isLoadingPlans}
              disabled={isPending}
            />

            {selectedPlan && (
              <PriceSummary
                amount={displayPrice}
                billingPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
                planName={selectedPlan.name}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ReactivateSubscriptionModal;
